import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCalendar, FiArrowRight, FiCheckCircle, FiClock,
  FiRepeat, FiBell
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const CalendarDocs = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/calendar" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Calendar</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
              <FiCalendar className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Calendar Integration</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Keep track of deadlines, schedule tasks, and manage your time effectively with Tasklina's calendar features.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Time Management Made Easy</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                The calendar in Tasklina automatically shows all tasks with due dates, helping you 
                visualize your workload across days, weeks, and months. Never miss a deadline again.
              </p>
            </div>
          </section>

          {/* Calendar Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Calendar Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiClock className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Multiple Views</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Switch between day, week, and month views</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiRepeat className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Recurring Tasks</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Set up tasks that repeat on schedule</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiBell className="w-6 h-6 text-orange-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Reminders</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Get notified before deadlines</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCalendar className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Drag & Drop</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Reschedule tasks by dragging</p>
              </div>
            </div>
          </section>

          {/* Using Calendar */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Using the Calendar</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Viewing Tasks</h3>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Click on "Calendar" in the sidebar navigation</li>
                  <li>All tasks with due dates appear on their respective dates</li>
                  <li>Color coding shows task priority or project</li>
                  <li>Click any task to view details or edit</li>
                </ol>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Rescheduling Tasks</h3>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Find the task on the calendar</li>
                  <li>Click and drag to a new date</li>
                  <li>Drop to update the due date</li>
                  <li>Task is automatically saved</li>
                </ol>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Creating Tasks from Calendar</h3>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Click on any date in the calendar</li>
                  <li>A quick-add task dialog appears</li>
                  <li>Enter task details and save</li>
                  <li>Task is created with that due date</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Google Calendar Integration */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Google Calendar Sync</h2>
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Connect Google Calendar</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Sync your Tasklina tasks with Google Calendar for a unified view of all your commitments.
              </p>
              <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-decimal list-inside">
                <li>Go to Settings → Integrations</li>
                <li>Click "Connect Google Calendar"</li>
                <li>Authorize Tasklina to access your calendar</li>
                <li>Choose sync preferences (one-way or two-way)</li>
              </ol>
            </div>
          </section>

          {/* Tips */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Calendar Tips</h2>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Use week view for sprint planning and daily work</span>
              </li>
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Month view helps identify busy periods and bottlenecks</span>
              </li>
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Set reminders to get notified before important deadlines</span>
              </li>
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Filter by project to focus on specific work areas</span>
              </li>
            </ul>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/views"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Views & Boards</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/features/notifications"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Notifications</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default CalendarDocs;
